import React, { useEffect, useState } from 'react';
import { Dialog } from '../../../shared/custom-ui';
import { V2Button } from '../../../shared/custom-ui/v2-components';
import Skeleton from '../../../shared/custom-ui/skeleton/Worklist/Skeleton';
import ApiServices from '../../../shared/ApiServices/ApiServices';
import { User, Stethoscope, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { buildPatientDisplayName, buildMemberDisplayName } from '../../../shared/utils/nameUtils';

const Icons = { User, Stethoscope, AlertCircle, CheckCircle2, Clock } as any;

type Props = { isOpen: boolean; onClose: () => void; onSuccess?: () => void; initialData?: Record<string, any>; isEditMode: boolean };

const defaultValues = { study_id: '', radiologist_id: '', status: '', study_priority: '', };

const ModernWorklistForm: React.FC<Props> = ({ isOpen, onClose, onSuccess, initialData = {}, isEditMode }) => {

    const [formData, setFormData] = useState({ ...defaultValues });
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState<any[]>([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [patients, setPatients] = useState<any[]>([]);
    const [patientsLoading, setPatientsLoading] = useState(false);

    useEffect(() => {
        const loadMinimalUsers = async () => {
            try {
                setMembersLoading(true);
                const res = await ApiServices.getMinimalUsers();
                setMembers(res.members || []);
            } catch (err) {
                console.error('Failed to load minimal users:', err);
            } finally {
                setMembersLoading(false);
            }
        };

        const loadAllPatients = async () => {
            try {
                setPatientsLoading(true);
                const res = await ApiServices.getAllStudies(1000);
                setPatients(res.items || []);
            } catch (err) {
                console.error('Failed to load patients:', err);
            } finally {
                setPatientsLoading(false);
            }
        };

        if (isOpen) {
            loadMinimalUsers();
            if (!isEditMode) loadAllPatients();
        }

        if (initialData) {
            setFormData({
                ...defaultValues,
                ...initialData,
                study_id: initialData.SK || initialData.study_id || '',
                radiologist_id:
                    initialData.current_rad_id ||
                    initialData.rad_id ||
                    initialData.radiologist_id ||
                    '',
                status: initialData.status || 'PENDING',
                study_priority: initialData.study_priority || 'NORMAL',
            });
        }
    }, [initialData, isOpen, isEditMode]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const study_id = initialData?.PK || formData.study_id;
            const current_rad_id = initialData?.rad_id;
            const new_rad_id = formData.radiologist_id;

            if (!study_id || !new_rad_id) {
                console.error("Missing study_id or radiologist_id");
                setLoading(false);
                return;
            }

            if (isEditMode) {
                if (current_rad_id === new_rad_id && formData.status === initialData?.status) {
                    setLoading(false);
                    return;
                }

                await ApiServices.updateWorklistTask({
                    study_id,
                    current_rad_id,
                    new_rad_id,
                    status: formData.status,
                    study_priority: formData.study_priority,
                });
            } else {
                await ApiServices.createWorklistTask({
                    study_id,
                    rad_id: new_rad_id,
                    status: formData.status,
                    study_priority: formData.study_priority,
                });
            }

            onSuccess?.();
            onClose();
        } catch (err) {
            console.error("Form submission failed:", err);
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'COMPLETED': return <Icons.CheckCircle2 className="w-5 h-5" />;
            case 'IN_PROGRESS': return <Icons.Clock className="w-5 h-5" />;
            case 'ASSIGNED': return <Icons.User className="w-5 h-5" />;
            default: return <Icons.AlertCircle className="w-5 h-5" />;
        }
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'COMPLETED':
                return 'bg-emerald-100 text-emerald-700 border-emerald-200';
            case 'IN_PROGRESS':
                return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'ASSIGNED':
                return 'bg-amber-100 text-amber-700 border-amber-200';
            case 'ON_HOLD':
                return 'bg-gray-100 text-gray-700 border-gray-200';
            default:
                return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const currentRadId = initialData?.current_rad_id || initialData?.rad_id || initialData?.SK || '';
    const disableUpdate = isEditMode &&
        formData.radiologist_id === currentRadId &&
        formData.status === initialData?.status &&
        formData.study_priority === (initialData?.study_priority || 'NORMAL');

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            size="large"
            title={isEditMode ? 'Update Task' : 'Create New Task'}
            description={
                isEditMode
                    ? 'Modify the assignee, status, or study for this worklist task.'
                    : 'Assign a radiologist to a patient study and create a new worklist task.'
            }
            footer={
                <div className="flex w-full justify-end gap-3">
                    <V2Button
                        variant="outline"
                        onClick={onClose}
                        disabled={loading}
                        size="medium"
                    >
                        Cancel
                    </V2Button>
                    <V2Button
                        type="submit"
                        form="modern-worklist-form"
                        loading={loading}
                        disabled={disableUpdate || loading}
                        size="medium"
                        className={`${disableUpdate ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                        {isEditMode ? 'Update Task' : 'Create Task'}
                    </V2Button>
                </div>
            }
        >
            {/* Patient Info Banner (Edit Mode) */}
            {initialData?.patient_name && (
                <div className="mb-6 animate-slideDown">
                    <div className="relative overflow-hidden rounded-xl border-2 p-5 shadow-md" style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-surface)' }}>
                        {/* Decorative Corner */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-bl-full transform translate-x-16 -translate-y-16"></div>

                        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-start gap-4">
                                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg flex-shrink-0">
                                    <Icons.User className="w-6 h-6 text-white" strokeWidth={2} />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-lg font-bold" style={{ color: 'var(--text-heading)' }}>
                                        {initialData?.patient_name || 'Unnamed Patient'}
                                    </h4>
                                    <div className="flex flex-wrap items-center gap-3 mt-2">
                                        <div className="flex items-center gap-2 text-sm">
                                            <span style={{ color: 'var(--text-muted)' }}>MRN:</span>
                                            <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                {initialData?.mrn_number || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-shrink-0">
                                <div
                                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold capitalize border shadow-sm ${getStatusBadgeClass(
                                        initialData?.status || 'PENDING'
                                    )}`}
                                >
                                    {getStatusIcon(initialData?.status || 'PENDING')}
                                    {initialData?.status
                                        ?.replace(/_/g, ' ')
                                        ?.toLowerCase()
                                        ?.replace(/\b\w/g, c => c.toUpperCase())}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Form */}
            <form id="modern-worklist-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Study Selector (Create Mode Only) */}
                    {!isEditMode && (
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                                <div className="flex items-center gap-2">
                                    <Icons.User className="w-4 h-4" />
                                    Select Study
                                </div>
                            </label>
                            <div className="relative">
                                {patientsLoading ? (
                                    <div className="space-y-2">
                                        <Skeleton height={48} />
                                        <div className="flex gap-2">
                                            <Skeleton height={20} width={60} />
                                            <Skeleton height={20} width={100} />
                                        </div>
                                    </div>
                                ) : (
                                    <select
                                        name="study_id"
                                        value={formData.study_id}
                                        onChange={handleChange}
                                        required
                                        className="w-full rounded-xl px-4 py-3 text-sm font-medium shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20" style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                                    >
                                        <option value="">Choose a patient study...</option>
                                        {patients.map((p: any) => (
                                            <option key={p.SK} value={p.SK}>
                                                {buildPatientDisplayName(p) || 'Unnamed'} ({p.SK})
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Radiologist Selector */}
                    <div>
                        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                            <div className="flex items-center gap-2">
                                <Icons.Stethoscope className="w-4 h-4" />
                                Assign Radiologist
                            </div>
                        </label>
                        <div className="relative">
                            {membersLoading ? (
                                <Skeleton height={48} />
                            ) : (
                                <select
                                    name="radiologist_id"
                                    value={formData.radiologist_id}
                                    onChange={handleChange}
                                    required
                                    className="w-full rounded-xl px-4 py-3 text-sm font-medium shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20" style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                                >
                                    <option value="">Choose a radiologist...</option>
                                    {members.map(member => {
                                        const label = buildMemberDisplayName(member) || member.email || member.rad_id;
                                        return (
                                            <option key={member.rad_id} value={member.rad_id}>
                                                {label}
                                            </option>
                                        );
                                    })}
                                </select>
                            )}
                        </div>
                    </div>

                    {/* Status Selector */}
                    <div>
                        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                            <div className="flex items-center gap-2">
                                <Icons.AlertCircle className="w-4 h-4" />
                                Task Status
                            </div>
                        </label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            required
                            className="w-full rounded-xl px-4 py-3 text-sm font-medium shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20" style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                        >
                            <option value="PENDING">Pending</option>
                            <option value="ASSIGNED">Assigned</option>
                            <option value="IN_PROGRESS">In Progress</option>
                            <option value="COMPLETED">Completed</option>
                            <option value="ON_HOLD">On Hold</option>
                            <option value="NEW">New</option>
                        </select>
                    </div>

                    {/* Priority Selector */}
                    <div>
                        <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                            <div className="flex items-center gap-2">
                                <Icons.AlertCircle className="w-4 h-4" />
                                Study Priority
                            </div>
                        </label>
                        <select
                            name="study_priority"
                            value={formData.study_priority}
                            onChange={handleChange}
                            required
                            className="w-full rounded-xl px-4 py-3 text-sm font-medium shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20" style={{ background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                        >
                            <option value="NORMAL">Normal</option>
                            <option value="URGENT">Urgent</option>
                            <option value="CRITICAL">Critical</option>
                        </select>
                    </div>
                </div>
            </form>
        </Dialog>
    );
};

export default ModernWorklistForm;
